import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  LockKeyIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  paymentService,
  validateIBAN,
  formatIBAN,
  cleanIBAN,
  validateAmount,
  validateOwnerName,
  validateReference,
} from '../../../shared/utils';
import { Breadcrumb } from '../components/Breadcrumb';
import type { RootState, AppDispatch } from '../store/store';
import {
  clearRetryPayment,
  deleteTemplate,
  setCheckoutSession,
} from '../store/paymentSlice';
import './PayRent.css';

// Rent is always paid in euros
const PAYMENT_CURRENCY = 'EUR';

interface FormErrors {
  amount?: string;
  ownerName?: string;
  ownerIban?: string;
  reference?: string;
}

export const PayRent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const retryData = useSelector((state: RootState) => state.payment.retryData);
  const templates = useSelector((state: RootState) => state.payment.templates);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: retryData?.amount ?? '',
    ownerName: retryData?.recipientName ?? '',
    ownerIban: '', // IBAN is never pre-filled — must be entered fresh each payment
    reference: retryData?.reference ?? '',
  });

  // Clear retry data from store once we've used it to pre-fill
  useEffect(() => {
    if (retryData) {
      dispatch(clearRetryPayment());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the browser restores this page from the back/forward cache (e.g. the user
  // hits "back" after completing a payment), the component isn't remounted, so
  // `loading` can still be stuck `true` from the previous submit — leaving the
  // Pay Now button permanently disabled/greyed out. Reset transient state on restore.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoading(false);
        setToast(null);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);
  const [errors, setErrors] = useState<FormErrors>({});
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field in errors && errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const amount = parseFloat(formData.amount);
    if (!formData.amount || !validateAmount(amount)) {
      newErrors.amount = t('payRent.validationAmountError');
    }

    if (!validateOwnerName(formData.ownerName)) {
      newErrors.ownerName = t('payRent.validationOwnerNameError');
    }

    const cleanedIban = cleanIBAN(formData.ownerIban);
    if (!validateIBAN(cleanedIban)) {
      newErrors.ownerIban = t('payRent.validationIbanError');
    }

    if (!validateReference(formData.reference)) {
      newErrors.reference = t('payRent.validationReferenceError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setToast(null);

    try {
      const response = await paymentService.createPayment({
        amount: parseFloat(formData.amount),
        currency: PAYMENT_CURRENCY,
        recipientName: formData.ownerName.trim(),
        recipientIban: cleanIBAN(formData.ownerIban),
        reference: formData.reference.trim(),
      });

      if (response.success && response.data?.sessionId) {
        setToast({
          message: t('payRent.paymentCreated'),
          type: 'success',
        });

        // Hand off to the /payment page, which mounts the PAY/Connect widget
        dispatch(
          setCheckoutSession({
            sessionId: response.data.sessionId,
            transactionId: response.data.paymentRequestId,
          })
        );
        setTimeout(() => {
          navigate({ to: '/payment' });
        }, 1500);
      } else {
        setToast({
          message: t('payRent.paymentFailed'),
          type: 'error',
        });
        setLoading(false);
      }
    } catch (err) {
      const error = err as { message?: string };
      console.error('Payment creation error:', error);
      setToast({
        message: error.message || t('payRent.errorOccurred'),
        type: 'error',
      });
      setLoading(false);
    }
  };

  const fieldClass = (error?: string) =>
    `mc-field${error ? ' mc-field--error' : ''}`;

  return (
    <>
      <Breadcrumb items={[{ label: t('app.nav.payRent') }]} />

      <div className="mc-heading">
        <div>
          <h1>{t('payRent.heading')}</h1>
          <p>{t('payRent.subtitle')}</p>
        </div>
      </div>

      <div className="mc-grid mc-cols2 mc-formmax pay-rent__grid">
        <section className="mc-card">
          {toast && (
            <div
              className={`mc-callout mc-callout--${toast.type} pay-rent__toast`}
              role="status"
            >
              {toast.message}
            </div>
          )}

          {templates.length > 0 && (
            <div className="pay-rent__templates">
              <span className="mc-muted mc-small">
                {t('payRent.savedTemplates')}
              </span>
              <div className="pay-rent__template-list">
                {templates.map(tmpl => (
                  <span key={tmpl.id} className="pay-rent__template">
                    <button
                      type="button"
                      className="pay-rent__template-use"
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          amount: tmpl.amount,
                          ownerName: tmpl.recipientName,
                          reference: tmpl.reference,
                          // ownerIban intentionally NOT set — user must enter it
                        }))
                      }
                    >
                      {tmpl.name}
                    </button>
                    <button
                      type="button"
                      className="pay-rent__template-remove"
                      aria-label={t('payRent.removeTemplate')}
                      title={t('payRent.removeTemplate')}
                      onClick={() => dispatch(deleteTemplate(tmpl.id))}
                    >
                      <XIcon size={12} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mc-grid mc-cols2 pay-rent__row">
              <label className={fieldClass(errors.amount)}>
                {t('payRent.amountLabel')} *
                <span className="pay-rent__amount">
                  <span className="pay-rent__currency" aria-hidden="true">
                    €
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder={t('payRent.amountPlaceholder')}
                    value={formData.amount}
                    onChange={e => updateField('amount', e.target.value)}
                    aria-invalid={!!errors.amount}
                    required
                  />
                </span>
                {errors.amount && (
                  <span className="mc-field__hint">{errors.amount}</span>
                )}
              </label>

              <label className={fieldClass(errors.ownerName)}>
                {t('payRent.ownerLabel')} *
                <input
                  type="text"
                  autoComplete="off"
                  placeholder={t('payRent.ownerNamePlaceholder')}
                  value={formData.ownerName}
                  onChange={e => updateField('ownerName', e.target.value)}
                  aria-invalid={!!errors.ownerName}
                  required
                />
                {errors.ownerName && (
                  <span className="mc-field__hint">{errors.ownerName}</span>
                )}
              </label>
            </div>

            <label className={fieldClass(errors.ownerIban)}>
              {t('payRent.ibanLabel')} *
              <input
                type="text"
                autoComplete="off"
                placeholder={t('payRent.ibanPlaceholder')}
                value={formData.ownerIban}
                onChange={e =>
                  updateField('ownerIban', formatIBAN(e.target.value))
                }
                aria-invalid={!!errors.ownerIban}
                required
              />
              <span className="mc-field__hint">
                {errors.ownerIban || t('payRent.ibanHelperText')}
              </span>
            </label>

            <label className={fieldClass(errors.reference)}>
              {t('payRent.referenceLabel')} *
              <input
                type="text"
                maxLength={140}
                placeholder={t('payRent.referencePlaceholder')}
                value={formData.reference}
                onChange={e => updateField('reference', e.target.value)}
                aria-invalid={!!errors.reference}
                required
              />
              <span className="mc-field__hint">
                {errors.reference || t('payRent.referenceHelperText')}
              </span>
            </label>

            <div className="mc-actions pay-rent__actions">
              <button
                type="button"
                className="mc-btn mc-btn--outline"
                onClick={() => navigate({ to: '/' })}
                disabled={loading}
              >
                {t('payRent.cancel')}
              </button>
              <button
                type="submit"
                className="mc-btn mc-btn--primary"
                disabled={loading}
              >
                {loading ? t('payRent.processing') : t('payRent.continue')}
                {!loading && <ArrowRightIcon size={18} weight="bold" />}
              </button>
            </div>
          </form>
        </section>

        <section className="mc-card mc-benefits">
          <h2>{t('payRent.benefitsTitle')}</h2>
          <ul className="mc-checklist">
            {['benefit1', 'benefit2', 'benefit3'].map(key => (
              <li key={key}>
                <CheckCircleIcon size={20} weight="fill" />
                {t(`payRent.${key}`)}
              </li>
            ))}
          </ul>
          <div className="mc-callout">
            <strong>
              <LockKeyIcon size={18} weight="bold" />
              {t('payRent.secureTitle')}
            </strong>
            <p>{t('payRent.secureText')}</p>
          </div>
        </section>
      </div>
    </>
  );
};
